import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { CoreBox } from '@youscience/core';
import { Theme, useMediaQuery } from '@mui/material';
import {
  carnegieSizeSetting,
  degree,
  enrollmentSize,
  institutionType,
  locales,
  specialty,
  areasDescriptionMap,
  majorsDescriptionMap,
  CollegeSearchFilters,
} from '@common-types';

import useSchoolSearchFiltersStore, { INITIAL_SCHOOL_SORT, SchoolSearchState } from '@store/SchoolSearchFiltersStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

import { useSchools } from '@hooks/useSchools';
import { useStateSpotlight } from '@hooks/useStateSpotlight';

import { MOBILE_PER_PAGE_OPTIONS } from '@constants';
import { SCHOOL_FILTERS_DATA } from '@constants/fullSchoolsList';
import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { STATES_OF_USA } from '@utils/states';
import {
  getSchoolSortOptions,
  STATE_PUBLIC_COLLEGE_FILTER,
  SchoolListSortOption,
  STATE_SPOTLIGHT_SCHOOL_SORT,
  getStatePublicCollegesLabel,
  getStatePublicCollegeFilter,
  getRestOfUsFilter,
  REST_OF_US_FILTER,
  REST_OF_US_LABEL,
} from '@constants/stateSpotlight';

import { Option, SelectedFiltersProps } from '@interfaces';
import { CollegeLocation } from '@interfaces/location';

import useAppInitStore from '@store/AppInitStore';

import { moneyFormatter } from '@utils/formatter';
import { getBreadcrumbs } from '@utils/breadcrumbs';

import { Container } from '@components/Layout/components/Container';
import { CardViewSwitcher } from '@components/CardViewSwitcher';
import { SchoolCard } from '@components/SchoolCard';
import { ReducedCardSkeleton } from '@components/ReducedSchoolCard/ReducedCardSkeleton';
import { LinearCardSkeleton } from '@components/LinearSchoolCard/LinearCardSkeleton';
import { EmptyStateText } from '@components/EmptyStateText';
import { Pagination } from '@components/Pagination';
import { StatePublicCollegesNotice } from '@components/StateSpotlight';
import SortBy, { SortByValues } from '@pages/Scholarships/components/SortBy';

import SearchFilesIcon from '@images/search-files.svg?react';

import Filters from './components/Filters';

import {
  sxStyles,
  StyledMainSectionActions,
  StyledCardViewSwitcherWrap,
  StyledSchoolCardsWrap,
  StyledFullRow,
  StyledDesktopFiltersWrap,
  StyledBottomPaginationWrap,
} from './SchoolList.styles';

import { FilterDrawer } from '@components/FilterDrawer/FilterDrawer';
import { ListHeader } from '@components/ListHeader/ListHeader';
import { ExploreModeSwitcher } from '@components/ExploreModeSwitcher/ExploreModeSwitcher';
import { HeaderActions } from '@components/HeaderActions/HeaderActions';

const SCHOOL_CARD_SKELETONS = Array.from({ length: 6 }, (el, index) => ({
  id: index,
}));

import { usePagination } from '@hooks/usePagination';
import { useMobileFilters } from '@hooks/useMobileFilters';

export interface CollegeSearchFormValues {
  states: string[];
  cipCodes: string[];
  carnegieSizeSettings: string[];
  institutionTypes: string[];
  locales: string[];
  specialty: string[];
  cost: number;
  hasScholarship: boolean;
}
export const SchoolList = () => {
  const location: CollegeLocation = useLocation();
  const { cipCode } = useParams();

  const filterByMajor = !!cipCode;

  const { schoolFavorites } = useUserPreferencesStore();

  const {
    filters,
    resetSearchFilters,
    removeSearchFilter,
    setSearchFilters,
    viewOptions,
    costFilter,
    setViewOptions,
    getAverageCostLimit,
    sortByValues,
    setSortByValues,
  } = useSchoolSearchFiltersStore((state) => state);

  const { spotlight, shouldApplyDefaults, markDefaultsApplied, hasCarriedFilters } = useStateSpotlight();
  const statePublicCollegeFilter = getStatePublicCollegeFilter(filters);

  const isMobileScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const isTabletScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { anchorMobileFilters, handleOpenMobileFilters, handleCloseMobileFilters } = useMobileFilters();

  const [searchFilterValue, setSearchFilterValue] = useState('');
  const [areaOfStudyTitle, setAreaOfStudyTitle] = useState('');

  const [isLinearView, setIsLinearView] = useState<boolean>(viewOptions.isLinearView || false);
  const [selectedValue, setSelectedValue] = useState<SelectedFiltersProps[]>([]);

  const currentPageName = cipCode ? BREADCRUMBS_PAGE_TEXT.SCHOOLS_BY_MAJOR : BREADCRUMBS_PAGE_TEXT.SCHOOLS;

  const { data: schoolsData = { schools: [], totalCount: 0, limit: 0 }, isLoading } = useSchools({ filterByMajor });
  const { isLoading: isInitialDataLoading } = useAppInitStore((store) => store);

  const { currentPage, paginationLimit, resetPaginationPage, handlePageSizeChange, handlePaginationChange } =
    usePagination({
      initialLimit: Number(filters.limit),
      initialSkip: Number(filters.skip || 0),
      isMobileScreen,
      isLoading,
      setSearchFilters,
      removeSearchFilter,
    });

  const pageBreadcrumbs = getBreadcrumbs(location.state?.from, currentPageName);

  const defaultValues = {
    carnegieSizeSettings: [],
    institutionTypes: [],
    states: [],
    cipCodes: [],
    specialty: [],
    awardLevels: [],
    locales: [],
    cost: costFilter?.max || 100000,
    hasScholarship: false,
  };

  const formValues = useForm<CollegeSearchFormValues>({
    defaultValues,
  });

  useEffect(() => {
    if (filterByMajor) {
      const areaOfStudyCode = cipCode.split('.')[0];
      const areaOfStudy = areasDescriptionMap.get(+areaOfStudyCode);

      setAreaOfStudyTitle(areaOfStudy?.cipAreaofStudy || '');

      if (filterByMajor) {
        setSearchFilters({ cipCodes: [cipCode] });
      }
    }
  }, [filterByMajor]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    setViewOptions({ isLinearView });
  }, [isLinearView]);

  useEffect(() => {
    const limit = isMobileScreen ? MOBILE_PER_PAGE_OPTIONS[0] : paginationLimit;

    if (filters.limit && filters.limit !== limit) {
      setSearchFilters({ limit });
    }
  }, [isMobileScreen]);

  const getSelectedFiltersFromState = () => {
    const options: SelectedFiltersProps[] = [];

    if (filters.keywords) {
      options.push({
        key: SCHOOL_FILTERS_DATA.search.fieldName,
        value: filters.keywords,
        label: filters.keywords,
      });
    }

    carnegieSizeSetting.forEach((option) => {
      if ((filters.carnegieSizeSettings as string[])?.includes(option.value)) {
        options.push({ ...option, key: SCHOOL_FILTERS_DATA.schoolsType.fieldNames.carnegieSizeSetting });
      }
    });

    institutionType.forEach((option) => {
      if ((filters.institutionTypes as string[])?.includes(option.value)) {
        options.push({ ...option, key: SCHOOL_FILTERS_DATA.schoolsType.fieldNames.institutionType });
      }
    });

    if (statePublicCollegeFilter && spotlight) {
      options.push({
        key: STATE_PUBLIC_COLLEGE_FILTER as keyof CollegeSearchFilters,
        value: statePublicCollegeFilter,
        label: getStatePublicCollegesLabel(spotlight.stateName),
      });
    }

    if (getRestOfUsFilter(filters) && spotlight) {
      options.push({
        key: REST_OF_US_FILTER as keyof CollegeSearchFilters,
        value: 'true',
        label: REST_OF_US_LABEL,
      });
    }

    (STATES_OF_USA as Option[]).forEach((option) => {
      if ((filters.states as string[])?.includes(option.value)) {
        options.push({ ...option, key: SCHOOL_FILTERS_DATA.location.fieldName });
      }
    });

    if (filterByMajor) {
      majorsDescriptionMap.forEach((cipCode) => {
        if ((filters.cipCodes as string[])?.includes(cipCode.cipCode.toString())) {
          options.push({
            label: cipCode.cipTitle,
            value: cipCode.cipCode,
            key: SCHOOL_FILTERS_DATA.areasOfStudy.fieldName,
          });
        }
      });
    } else {
      areasDescriptionMap.forEach((cipCode) => {
        if ((filters.cipCodes as string[])?.includes(cipCode.cipCode.toString())) {
          options.push({
            label: cipCode.cipTitle,
            value: cipCode.cipCode.toString(),
            key: SCHOOL_FILTERS_DATA.areasOfStudy.fieldName,
          });
        }
      });
    }

    (specialty as Option[]).forEach((option) => {
      if ((filters.specialty as string[])?.includes(option.value)) {
        options.push({ ...option, key: SCHOOL_FILTERS_DATA.specialty.fieldName });
      }
    });

    (degree as Option[]).forEach((option) => {
      if ((filters.awardLevels as string[])?.includes(option.value)) {
        options.push({ ...option, key: SCHOOL_FILTERS_DATA.degree.fieldName });
      }
    });

    (enrollmentSize as Option[]).forEach((option) => {
      if ((filters.totalStudentsEnrolled as string[])?.includes(option.value)) {
        options.push({ ...option, key: SCHOOL_FILTERS_DATA.enrollmentSize.fieldName });
      }
    });

    if (filters.cost) {
      const { cost } = filters;

      options.push({
        key: 'cost',
        value: `${cost}`,
        label: `${moneyFormatter.format(cost)}`,
      });
    }

    if (filters.hasScholarship) {
      options.push({
        key: 'hasScholarship',
        value: `${filters.hasScholarship}`,
        label: SCHOOL_FILTERS_DATA.hasScholarship.title,
      });
    }

    (locales as Option[]).forEach((option) => {
      if ((filters.locales as string[])?.includes(option.value)) {
        options.push({ ...option, key: SCHOOL_FILTERS_DATA.settings.fieldName });
      }
    });

    return options;
  };

  const loadFormValues = () => {
    const initialFormValue: { [k: string]: number | string[] | boolean } = defaultValues;

    const options = getSelectedFiltersFromState();

    options.forEach((opt) => {
      if (opt.key === SCHOOL_FILTERS_DATA.averageCost.fieldName) {
        initialFormValue[opt.key] = +opt.value;

        return;
      }
      initialFormValue[opt.key] = initialFormValue[opt.key]
        ? (initialFormValue[opt.key] as string[]).concat(opt.value)
        : [opt.value];
    });

    formValues.reset(initialFormValue);

    const keywords = filters.keywords as string;

    if (keywords) {
      setSearchFilterValue(keywords);
    }
  };

  useEffect(() => {
    formValues.setValue('cost', costFilter?.max || 100000);
  }, [costFilter.max]);

  useEffect(() => {
    loadFormValues();
  }, [selectedValue]);

  useEffect(() => {
    void getAverageCostLimit();

    if (Object.keys(filters).length > 0) {
      const options = getSelectedFiltersFromState();

      setSelectedValue(options);
    }
  }, []);

  useEffect(() => {
    if (!isInitialDataLoading) {
      if (filterByMajor) {
        resetSearchFilters({ cipCodes: [cipCode] });
      } else {
        setSearchFilters({ limit: paginationLimit });
      }
    }
  }, [isInitialDataLoading]);

  useEffect(() => {
    if (isInitialDataLoading || filterByMajor || !spotlight || !shouldApplyDefaults('schools')) {
      return;
    }

    if (!hasCarriedFilters('schools')) {
      setSearchFilters({ [STATE_PUBLIC_COLLEGE_FILTER]: [spotlight.stateCode] });
    }

    if (sortByValues.sort === INITIAL_SCHOOL_SORT.sort) {
      setSortByValues(STATE_SPOTLIGHT_SCHOOL_SORT);
    }

    markDefaultsApplied('schools');
  }, [isInitialDataLoading, spotlight]);

  const handleChangeSort = (values: SortByValues<SchoolListSortOption>) => {
    setSortByValues(values);
    resetPaginationPage();
  };

  const handleChangeFilters = (filter: keyof SchoolSearchState) => (values: string[]) => {
    const sorted = values.sort((a, b) => a.localeCompare(b));

    if (values.length) {
      setSearchFilters({ [filter]: sorted });
    } else {
      removeSearchFilter(filter);
    }

    resetPaginationPage();
  };

  useEffect(() => {
    const options = getSelectedFiltersFromState();

    setSelectedValue(options);
  }, [filters, spotlight]);

  const renderFilters = (isExpanded?: boolean) => {
    return (
      <Filters
        isExpanded={isExpanded}
        areaOfStudyTitle={areaOfStudyTitle}
        paginationLimit={paginationLimit}
        selectedValue={selectedValue}
        defaultValues={defaultValues}
        searchFilterValue={searchFilterValue}
        formValues={formValues}
        handleChangeFilters={handleChangeFilters}
        resetPaginationPage={resetPaginationPage}
        setSearchFilterValue={setSearchFilterValue}
      />
    );
  };

  const switcherComponent = !filterByMajor && <ExploreModeSwitcher />;
  const headerActions = (
    <HeaderActions
      isTabletScreen={isTabletScreen}
      favoritesCount={schoolFavorites?.length || 0}
      onOpenMobileFilters={handleOpenMobileFilters}
      mode="schools"
    />
  );

  const fullDescription =
    'Everyone has talent. And right here, you can dig in to more than 6,000 schools and find the perfect place to expand on yours.';
  const mobileDescription = 'Explore over 6,000 schools to find your perfect match.';

  return (
    <CoreBox sx={sxStyles.schoolsBg}>
      <ListHeader
        title={filterByMajor ? `Colleges that offer majors in ${areaOfStudyTitle}` : 'Explore education opportunities'}
        description={isMobileScreen ? mobileDescription : fullDescription}
        breadcrumbs={pageBreadcrumbs}
        actions={headerActions}
        switcherComponent={switcherComponent}
      />

      <Container sx={sxStyles.mainSectionWrap}>
        <StyledDesktopFiltersWrap>{renderFilters()}</StyledDesktopFiltersWrap>

        <CoreBox id="mainSectionContent" sx={sxStyles.mainSectionContent}>
          <StyledMainSectionActions>
            <CoreBox sx={sxStyles.listActionsStart}>
              <StyledCardViewSwitcherWrap>
                <CardViewSwitcher isLinearView={isLinearView} setIsLinearView={setIsLinearView} />
              </StyledCardViewSwitcherWrap>

              <SortBy<SchoolListSortOption>
                sortByValues={sortByValues}
                sortByOptions={getSchoolSortOptions(spotlight?.stateName)}
                setSortByFields={handleChangeSort}
                dataTestId="schoolSortBySelect"
              />
            </CoreBox>

            <Pagination
              onPageSizeChange={handlePageSizeChange}
              onPaginationChange={handlePaginationChange}
              page={currentPage}
              limit={paginationLimit}
              totalCount={schoolsData.totalCount}
              isLoading={isLoading}
            />
          </StyledMainSectionActions>

          {statePublicCollegeFilter && spotlight && <StatePublicCollegesNotice spotlight={spotlight} />}

          <StyledSchoolCardsWrap data-testid="schoolCardWrap" isGridView={!isLinearView}>
            {(isLoading || isInitialDataLoading) && (
              <>
                {SCHOOL_CARD_SKELETONS.map((skeletonCard) =>
                  isLinearView ? (
                    <LinearCardSkeleton key={skeletonCard.id} />
                  ) : (
                    <ReducedCardSkeleton key={skeletonCard.id} isFluid />
                  ),
                )}
              </>
            )}

            {!isLoading && !isInitialDataLoading && (
              <>
                {schoolsData.schools?.map((school) =>
                  isLinearView && !isMobileScreen ? (
                    <SchoolCard
                      key={school.id}
                      school={school}
                      schoolCardType="linear"
                      componentId="school-list-card"
                    />
                  ) : (
                    <SchoolCard
                      key={school.id}
                      school={school}
                      schoolCardType="reduced"
                      componentId="school-list-card"
                      isFluid
                    />
                  ),
                )}

                {schoolsData.schools?.length > 0 && (
                  <StyledBottomPaginationWrap>
                    <CoreBox />
                    <Pagination
                      onPageSizeChange={handlePageSizeChange}
                      onPaginationChange={handlePaginationChange}
                      page={currentPage}
                      limit={paginationLimit}
                      totalCount={schoolsData.totalCount}
                      isLoading={isLoading}
                    />
                  </StyledBottomPaginationWrap>
                )}

                {schoolsData.schools?.length === 0 && (
                  <StyledFullRow>
                    <EmptyStateText
                      icon={<SearchFilesIcon />}
                      title="No results"
                      subtitle="Hmmm. Looks like we couldn't find any matches. Please update the search field or remove some filters and try again."
                    />
                  </StyledFullRow>
                )}
              </>
            )}
          </StyledSchoolCardsWrap>
        </CoreBox>
      </Container>

      <FilterDrawer open={Boolean(anchorMobileFilters)} onClose={handleCloseMobileFilters}>
        {renderFilters(false)}
      </FilterDrawer>
    </CoreBox>
  );
};
